import React, { useRef, useState } from 'react';
import type { QuoteFormData, QuoteFormErrors, UploadedImage } from '../../types/quote';
import { FiUploadCloud, FiTrash2, FiAlertCircle } from 'react-icons/fi';
import { MuiTextInput } from './MuiInputs';

interface StepProductDetailsProps {
  data: QuoteFormData;
  errors: QuoteFormErrors;
  onChange: (field: keyof QuoteFormData, value: any) => void;
}

const MAX_IMAGES = 5;
const MAX_FILE_SIZE_MB = 10;

import { readFileAsDataUrl } from '../../utils/imageUtils';
export { readFileAsDataUrl };

export const StepProductDetails: React.FC<StepProductDetailsProps> = ({
  data,
  errors,
  onChange,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleFiles = async (files: FileList | File[]) => {
    setUploadError(null);
    const currentImages = data.productImages || [];
    const remainingSlots = MAX_IMAGES - currentImages.length;

    if (remainingSlots <= 0) {
      setUploadError(`Maximum of ${MAX_IMAGES} images allowed.`);
      return;
    }

    const filesArray = Array.from(files);
    const validNewImages: UploadedImage[] = [];

    for (const file of filesArray) {
      if (validNewImages.length >= remainingSlots) {
        setUploadError(`Only ${remainingSlots} more image(s) could be added. Max is ${MAX_IMAGES}.`);
        break;
      }

      if (!file.type.startsWith('image/')) {
        setUploadError(`"${file.name}" is not an image. Please upload image files only.`);
        continue;
      }

      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        setUploadError(`"${file.name}" exceeds the ${MAX_FILE_SIZE_MB}MB size limit.`);
        continue;
      }

      const previewUrl = URL.createObjectURL(file);
      const dataUrl = await readFileAsDataUrl(file);

      validNewImages.push({
        id: `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
        name: file.name,
        size: file.size,
        previewUrl,
        dataUrl,
        file,
      });
    }

    if (validNewImages.length > 0) {
      onChange('productImages', [...currentImages, ...validNewImages]);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleRemoveImage = (id: string) => {
    const updated = (data.productImages || []).filter((img) => img.id !== id);
    onChange('productImages', updated);
  };

  return (
    <div className="space-y-6">
      <div className="border-b border-gray-100 pb-4">
        <span className="text-xs uppercase tracking-widest text-[#F55109] font-bold">
          Step 4 of 6
        </span>
        <h3 className="text-2xl sm:text-3xl font-bold text-[#2C2C2C] special-header mt-1">
          SECTION 4 — PRODUCT & DELIVERY DETAILS
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          Describe the items you are transporting and attach photos if available.
        </p>
      </div>

      <div className="space-y-6">
        {/* What are you sending? (MUI Long Answer - Required) */}
        <MuiTextInput
          id="whatAreYouSending"
          label="What are you sending? (Long Answer)"
          required
          multiline
          rows={4}
          value={data.whatAreYouSending}
          onChange={(val) => onChange('whatAreYouSending', val)}
          placeholder="e.g. 5 boxes of retail apparel, estimated weight 45kg total, dimensions ~40x40x30cm each"
          errorMessage={errors.whatAreYouSending}
        />

        {/* Upload Product Image(s) (Optional - Maximum 5 files) */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-semibold text-gray-800">
              Upload Product Image(s){' '}
              <span className="text-xs font-normal text-gray-500">
                (Optional — Images — Maximum 5 files)
              </span>
            </label>
            <span className="text-xs font-semibold px-2.5 py-1 bg-gray-100 text-gray-600 rounded-full">
              {(data.productImages || []).length} / {MAX_IMAGES} uploaded
            </span>
          </div>

          {/* Hidden File Input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => {
              if (e.target.files && e.target.files.length > 0) {
                handleFiles(e.target.files);
                e.target.value = '';
              }
            }}
          />

          {/* Drag & Drop Area */}
          {(data.productImages || []).length < MAX_IMAGES && (
            <div
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-2xl p-6 sm:p-8 text-center cursor-pointer transition-all duration-200 ${
                dragActive
                  ? 'border-[#F55109] bg-[#F55109]/5 ring-2 ring-[#F55109]/20'
                  : 'border-gray-200 hover:border-[#F55109]/50 hover:bg-gray-50'
              }`}
            >
              <div className="w-12 h-12 mx-auto rounded-full bg-[#F55109]/10 text-[#F55109] flex items-center justify-center mb-3">
                <FiUploadCloud className="w-6 h-6" />
              </div>
              <p className="text-sm font-semibold text-gray-800">
                Click to upload or drag & drop product images
              </p>
              <p className="text-xs text-gray-400 mt-1">
                PNG, JPG, WEBP, or SVG (Up to {MAX_IMAGES} images, max {MAX_FILE_SIZE_MB}MB each)
              </p>
            </div>
          )}

          {uploadError && (
            <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-600 flex items-center gap-2">
              <FiAlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{uploadError}</span>
            </div>
          )}

          {/* Uploaded Images List */}
          {(data.productImages || []).length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 mt-4">
              {data.productImages.map((img) => (
                <div
                  key={img.id}
                  className="relative group bg-gray-50 rounded-xl border border-gray-200 overflow-hidden shadow-sm"
                >
                  <div className="aspect-square w-full overflow-hidden bg-gray-100 flex items-center justify-center">
                    <img
                      src={img.previewUrl}
                      alt={img.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                    />
                  </div>
                  <div className="p-2">
                    <p className="text-[11px] font-medium text-gray-800 truncate" title={img.name}>
                      {img.name}
                    </p>
                    <p className="text-[10px] text-gray-400">{formatFileSize(img.size)}</p>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveImage(img.id);
                    }}
                    className="absolute top-1.5 right-1.5 w-6 h-6 bg-red-600 text-white rounded-full flex items-center justify-center opacity-90 hover:opacity-100 shadow transition-opacity"
                    title="Remove image"
                    aria-label={`Remove ${img.name}`}
                  >
                    <FiTrash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Special Delivery Instructions (MUI Long Answer - Optional) */}
        <MuiTextInput
          id="specialDeliveryInstructions"
          label="Special Delivery Instructions (Long Answer — Optional)"
          multiline
          rows={3}
          value={data.specialDeliveryInstructions}
          onChange={(val) => onChange('specialDeliveryInstructions', val)}
          placeholder="e.g. Call 10 minutes before arrival, leave at security desk if recipient is in a meeting, entrance via Gate 3"
        />
      </div>
    </div>
  );
};
